window.theme = window.theme || {};

/*
 * theme.cart — Unified Cart API client
 * =====================================
 * All cart operations are funnelled through this namespace.
 * Consumers must NOT call /cart/*.js directly.
 *
 * Methods:
 *   theme.cart.add(items, options?)        → Promise<CartResponse>
 *   theme.cart.change(key, qty, options?)  → Promise<CartResponse>
 *   theme.cart.update(updates, options?)   → Promise<CartResponse>
 *   theme.cart.clear(options?)             → Promise<CartResponse>
 *   theme.cart.fetch(options?)             → Promise<CartResponse>
 *
 * Shared error state (runtime-only, cleared on page reload):
 *   theme.cart.errors.setLine(key, msg)          — persist a line error
 *   theme.cart.errors.clearLine(key)             — clear a line error
 *   theme.cart.errors.getLine(key)               → String | undefined
 *   theme.cart.errors.eachLine(cb)               — forEach(key, msg)
 *   theme.cart.errors.setFooter(msg, type)       — persist a footer error
 *   theme.cart.errors.clearFooter()              — clear footer error
 *   theme.cart.errors.getFooter()                → { message, type } | null
 *   theme.cart.errors.setProductForm(vid, msg)   — persist a product-form error
 *   theme.cart.errors.clearProductForm(vid)      — clear product-form error
 *   theme.cart.errors.getProductForm(vid)        → String | undefined
 *
 * Helpers:
 *   theme.cart.getSectionsToRender()       → Array<{ id, section, selector }>
 *   theme.cart.errorMessage(error, scope?) → String (i18n)
 *
 * options shape (all methods):
 *   { sections?, sectionsUrl?, openDrawer?, _internal?, _retried?, _silent? }
 *
 * _silent (change() only): suppresses CART_UPDATE/CART_REMOVE + success toast for
 * this call, for callers orchestrating multiple ops into a single downstream render
 * (e.g. cart-variant-selector's change+add pair).
 *
 * Error feedback routing (publishError):
 *   scope 'item'   → CART_ITEM_ERROR { key, message, type }  console.warn [cart][item]
 *   scope 'cart'   → CART_FOOTER_ERROR { message, type }     console.warn [cart][footer]
 *   scope 'silent' → no UI, auto-sync silently               console.error [cart][silent]
 *   All scopes also publish CART_ERROR (legacy backwards-compat).
 *
 * Supported error types and scope routing:
 *   item  : out_of_stock, stock_problems, stock_clamped, max_quantity_exceeded, max_already_reached,
 *           min_quantity_required, requires_selling_plan, invalid_selling_plan,
 *           missing_properties, bundle_unavailable, gift_card_recipient_required,
 *           product_unpublished, variant_unpublished
 *           (degrades to cart scope when lineKey/variantId unknown)
 *   cart  : cart_token_mismatch, cart_lock (+ auto-reload 2s), discount_expired,
 *           discount_not_eligible, discount_already_applied, shipping_unavailable,
 *           empty_cart, server_error_5xx
 *   silent: network, throttle, generic, fetch_timeout
 *
 * Success toasts:
 *   Gated by theme.settings.cartActionToastsEnable AND options._internal !== true.
 *   Auto-sync, retry, and internal flows pass _internal: true to suppress toasts.
 *
 * Events published (theme.pubsub.events):
 *   CART_REQUEST_START  — before every top-level request
 *   CART_REQUEST_END    — after every top-level request (success or error)
 *   CART_UPDATE         — on success (action: add|change|update|clear|sync)
 *   CART_ADD            — on add success
 *   CART_REMOVE         — on change/update that sets quantity to 0
 *   CART_CLEAR          — on clear success
 *   CART_ITEM_ERROR     — stock/availability error scoped to a line item
 *   CART_FOOTER_ERROR   — cart-level error (discount, session, etc.)
 *   CART_ERROR          — legacy backwards-compat (always published on error)
 *   CART_DRAWER_OPEN    — when add() resolves and openDrawer is true
 */

theme.cart = (() => {
  const MAX_SECTIONS_PER_REQUEST = 5;
  const MAX_RETRIES = 3;
  const RETRY_DELAYS = [250, 500, 1000];

  const pendingChanges = new Map();
  const sectionCache = new Map();

  class CartError extends Error {
    constructor({ type = 'generic', message = '', originalError = null } = {}) {
      super(message);
      this.name = 'CartError';
      this.type = type;
      this.originalError = originalError;
    }
  }

  function flattenErrors(errors) {
    if (!errors) return '';
    if (typeof errors === 'string') return errors;
    if (Array.isArray(errors)) return errors.join(' ');
    if (typeof errors === 'object') return Object.values(errors).flat().join(' ');
    return '';
  }

  const ERROR_PATTERNS = [
    { type: 'cart_token_mismatch',          statusCheck: 'cart_token_mismatch', keywords: ['token', 'session expired', 'sesión del carrito ha expirado'] },
    { type: 'cart_lock',                    keywords: ['modified', 'another session', 'another window', 'otra sesión', 'otra ventana', 'outra janela', 'anderem fenster', 'autre fenêtre', 'altra finestra'] },
    { type: 'discount_expired',             keywords: ['expired', 'expirado', 'expirou', 'abgelaufen', 'expiré', 'scaduto'] },
    { type: 'discount_not_eligible',        keywords: ['not eligible', 'not applicable', 'no aplicable', 'no aplica', "n'est pas éligible", 'non idoneo', 'não elegível', 'nicht anwendbar'] },
    { type: 'discount_already_applied',     keywords: ['already applied', 'ya está aplicado', 'ya aplicado', 'déjà appliqué', 'bereits angewendet', 'già applicato'] },
    { type: 'requires_selling_plan',        keywords: ['selling plan is required', 'requires selling plan', 'requires a selling plan'] },
    { type: 'invalid_selling_plan',         keywords: ['selling plan is invalid', 'selling plan not available', 'invalid selling plan'] },
    { type: 'missing_properties',           keywords: ['required properties', 'property is required', 'missing properties', 'propiedad requerida', 'propriedade obrigatória', 'propriété requise'] },
    { type: 'bundle_unavailable',           keywords: ['bundle'] },
    { type: 'gift_card_recipient_required', keywords: ['gift card recipient', 'recipient email required', 'recipient is required', 'destinatario'] },
    { type: 'shipping_unavailable',         keywords: ['cannot ship', 'shipping not available', 'no se envía', 'ne livre pas', 'non spedisce', 'ist nicht versandbar'] },
    { type: 'empty_cart',                   keywords: ['cart is empty', 'carrito está vacío', 'carrito vacío', 'carrinho vazio', 'warenkorb ist leer', 'panier est vide', 'carrello è vuoto'] },
    { type: 'max_already_reached',          keywords: ['already at maximum', 'already have the maximum', 'already has the maximum', 'maximum quantity reached', 'already at the limit', 'cantidad máxima', 'máxima de este artículo', 'máximo de este artículo', 'ya tienes el máximo', 'ya tiene el máximo', 'já tem o máximo', 'maximale menge erreicht', 'bereits die höchstmenge', 'quantité maximale', 'déjà au maximum', 'avez déjà le maximum', 'quantità massima', 'hai già il massimo'] },
    { type: 'max_quantity_exceeded',        keywords: ['exceeds maximum', 'exceeds the maximum', 'limit per order', 'purchase limit', 'límite por', 'höchstmenge', 'limite massimo', 'limite par commande'] },
    { type: 'min_quantity_required',        keywords: ['minimum quantity', 'below minimum', 'cantidad mínima', 'quantité minimum', 'quantità minima', 'quantidade mínima', 'mindestmenge'] },
    { type: 'product_unpublished',          keywords: ['product no longer available', 'ya no está disponible', "n'est plus disponible", 'non più disponibile', 'nicht mehr verfügbar', 'já não está disponível'] },
    { type: 'variant_unpublished',          keywords: ['variant no longer available', 'variant has been removed', 'variant not found'] },
    { type: 'out_of_stock',                 keywords: ['all variants', 'sold out', 'agotado'] },
    { type: 'stock_problems',               keywords: ['stock', 'quantity', 'inventar'] },
    { type: 'stock_problems',               keywords: ['availabil', 'available', 'added', 'añadi'], requireDigit: true },
  ];

  function inferErrorType(payload) {
    const combined = (
      (payload.description || '') + ' ' +
      (payload.message || '') + ' ' +
      flattenErrors(payload.errors)
    ).toLowerCase();

    const match = ERROR_PATTERNS.find(p => {
      if (p.statusCheck && payload.status === p.statusCheck) return true;
      if (p.keywords.some(k => combined.includes(k))) {
        return p.requireDigit ? /\d/.test(combined) : true;
      }
      return false;
    });
    return match?.type || 'generic';
  }

  let cartInflight = null;

  const getSectionsUrl = (sectionsUrl) => sectionsUrl || window.Shopify?.routes?.root || '/';

  async function request(url, fetchOptions, retryCount = 0) {
    const isMutation = fetchOptions?.method !== 'GET';
    if (isMutation) cartInflight = null;

    if (retryCount === 0) {
      theme.pubsub.publish(theme.pubsub.events.CART_REQUEST_START, { url });
    }

    try {
      let response;

      const timeoutController = new AbortController();
      const timeoutId = setTimeout(() => timeoutController.abort(), 30000);

      const callerSignal = fetchOptions?.signal;
      const effectiveSignal = callerSignal && typeof AbortSignal.any === 'function'
        ? AbortSignal.any([callerSignal, timeoutController.signal])
        : timeoutController.signal;

      try {
        response = await fetch(url, { ...fetchOptions, signal: effectiveSignal });
      } catch (fetchError) {
        if (fetchError.name === 'AbortError') {
          if (timeoutController.signal.aborted) {
            throw new CartError({ type: 'fetch_timeout', message: 'Request exceeded 30s' });
          }
          throw fetchError;
        }
        throw new CartError({ type: 'network', message: fetchError.message, originalError: fetchError });
      } finally {
        clearTimeout(timeoutId);
      }

      if (response.status === 429) {
        if (retryCount < MAX_RETRIES) {
          const retryAfter = parseInt(response.headers.get('Retry-After') || '0', 10);
          const delay = retryAfter > 0 ? retryAfter * 1000 : RETRY_DELAYS[retryCount];
          await sleep(delay);
          return request(url, fetchOptions, retryCount + 1);
        }
        throw new CartError({ type: 'throttle', message: 'Too many requests' });
      }

      if (response.status === 422) {
        const payload = await response.json().catch(() => ({}));
        const type = inferErrorType(payload);
        const message = payload.description || payload.message || flattenErrors(payload.errors) || '';
        throw new CartError({ type, message, originalError: payload });
      }

      if (response.status >= 500 && response.status <= 599) {
        throw new CartError({ type: 'server_error_5xx', message: `HTTP ${response.status}`, originalError: { status: response.status } });
      }

      if (!response.ok) {
        throw new CartError({ type: 'generic', message: `HTTP ${response.status}` });
      }

      const data = await response.json();
      return data;
    } finally {
      if (isMutation) cartInflight = null;
      if (retryCount === 0) {
        theme.pubsub.publish(theme.pubsub.events.CART_REQUEST_END, { url });
      }
    }
  }

  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  function extractMaxFromDescription(description) {
    if (!description) return null;
    const match = String(description).match(/\b(\d+)\b/);
    const n = match ? parseInt(match[1], 10) : null;
    return n > 0 ? n : null;
  }

  async function autoSyncCart() {
    try {
      const sections = getSectionsToRender();
      if (!sections.length) return;
      const data = await fetchCart({ sections });
      theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, { action: 'sync', cart: data, sections: data.sections });
    } catch (_) {}
  }

  function buildSectionsParam(sections) {
    if (!sections || sections.length === 0) return null;
    const ids = sections.map(s => (typeof s === 'string' ? s : s.section || s.id)).filter(Boolean);
    return ids;
  }

  async function fetchSectionsChunked(sectionIds, sectionsUrl) {
    const url = getSectionsUrl(sectionsUrl);
    const uniqueIds = [...new Set(sectionIds)];
    const chunks = [];

    for (let i = 0; i < uniqueIds.length; i += MAX_SECTIONS_PER_REQUEST) {
      chunks.push(uniqueIds.slice(i, i + MAX_SECTIONS_PER_REQUEST));
    }

    const responses = await Promise.all(
      chunks.map(async (chunk) => {
        const response = await fetch(`${url}?sections=${chunk.join(',')}`);
        return response.ok ? response.json() : {};
      })
    );

    return Object.assign({}, ...responses);
  }

  function cacheSections(sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([key, html]) => sectionCache.set(key, html));
  }

  function resolveScope(error, context) {
    if (context.scope) return context.scope;
    const type = error instanceof CartError ? error.type : 'generic';

    if (['network', 'throttle', 'generic', 'fetch_timeout'].includes(type)) return 'silent';

    const cartTypes = ['cart_token_mismatch', 'cart_lock', 'discount_expired', 'discount_not_eligible', 'discount_already_applied', 'shipping_unavailable', 'empty_cart', 'server_error_5xx'];
    if (cartTypes.includes(type)) return 'cart';

    const itemTypes = ['out_of_stock', 'stock_problems', 'stock_clamped', 'max_quantity_exceeded', 'max_already_reached', 'min_quantity_required', 'requires_selling_plan', 'invalid_selling_plan', 'missing_properties', 'bundle_unavailable', 'gift_card_recipient_required', 'product_unpublished', 'variant_unpublished'];
    if (itemTypes.includes(type) && (context.lineKey || context.variantId)) return 'item';
    if (itemTypes.includes(type)) return 'cart';

    return 'silent';
  }

  function publishError(error, context = {}) {
    const type = error instanceof CartError ? error.type : 'generic';
    const legacyPayload = { type, message: error.message, originalError: error };

    theme.pubsub.publish(theme.pubsub.events.CART_ERROR, legacyPayload);

    const scope = resolveScope(error, context);

    if (scope === 'silent') {
      console.error('[cart][silent]', error);
      return;
    }

    if (scope === 'item') {
      console.warn('[cart][item]', error);
      const msg = errorMessage(error, 'line');
      if (context.lineKey) errors.setLine(context.lineKey, msg);
      if (context.variantId) errors.setProductForm(context.variantId, msg);
      theme.pubsub.publish(theme.pubsub.events.CART_ITEM_ERROR, {
        key: context.lineKey,
        variantId: context.variantId,
        message: msg,
        type,
      });
      return;
    }

    if (scope === 'cart') {
      console.warn('[cart][footer]', error);
      const msg = errorMessage(error, 'footer');
      errors.setFooter(msg, type);
      theme.pubsub.publish(theme.pubsub.events.CART_FOOTER_ERROR, {
        message: msg,
        type,
      });
    }
  }

  function getSectionsToRender() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.getSectionsToRender) {
      return cartDrawer.getSectionsToRender();
    }
    return [];
  }

  function errorMessage(error, scope) {
    const locales = theme.locales?.cart;
    if (!locales) return error.message || '';

    const type = error instanceof CartError ? error.type : 'generic';

    if (scope === 'line') {
      const count = extractMaxFromDescription(error.originalError?.description || error.message || flattenErrors(error.originalError?.errors));
      const withCount = (tpl) => {
        if (!tpl) return null;
        return count ? tpl.replace('[count]', String(count)) : tpl;
      };
      const lineMap = {
        out_of_stock: locales.lineError?.outOfStock,
        stock_problems: locales.lineError?.onlyNAvailable,
        stock_clamped: locales.lineError?.onlyNAvailable,
        max_reached: locales.lineError?.maxReached,
        max_already_reached: locales.lineError?.maxAlreadyReached,
        cannot_add: locales.lineError?.cannotAdd,
        generic: locales.lineError?.generic,
        bundle_unavailable: locales.lineError?.bundleUnavailable,
        gift_card_recipient_required: locales.lineError?.giftCardRecipientRequired,
        invalid_selling_plan: locales.lineError?.invalidSellingPlan,
        max_quantity_exceeded: withCount(locales.lineError?.maxQuantityExceeded) || locales.lineError?.generic,
        min_quantity_required: withCount(locales.lineError?.minQuantityRequired) || locales.lineError?.generic,
        missing_properties: locales.lineError?.missingProperties,
        product_unpublished: locales.lineError?.productUnpublished,
        requires_selling_plan: locales.lineError?.requiresSellingPlan,
        variant_unpublished: locales.lineError?.variantUnpublished,
      };
      return lineMap[type] || locales.lineError?.generic || error.message || '';
    }

    if (scope === 'footer') {
      const footerMap = {
        invalid_discount: locales.footerError?.invalidDiscount,
        cart_token_mismatch: locales.footerError?.sessionExpired,
        generic: locales.footerError?.generic,
        cart_lock: locales.footerError?.cartLock,
        discount_expired: locales.footerError?.discountExpired,
        discount_not_eligible: locales.footerError?.discountNotEligible,
        discount_already_applied: locales.footerError?.discountAlreadyApplied,
        shipping_unavailable: locales.footerError?.shippingUnavailable,
        empty_cart: locales.footerError?.emptyCart,
        server_error_5xx: locales.footerError?.serverError5xx,
      };
      return footerMap[type] || locales.footerError?.generic || error.message || '';
    }

    const map = {
      out_of_stock: locales.error?.outOfStock,
      stock_problems: locales.error?.stockProblems,
      network: locales.error?.network,
      throttle: locales.error?.throttle,
      cart_token_mismatch: locales.error?.tokenMismatch,
      generic: locales.error?.generic,
    };

    return map[type] || locales.error?.generic || error.message || '';
  }

  function showSuccessToast(message, options = {}) {
    if (options._internal) return;
    if (!theme.settings?.cartActionToastsEnable) return;
    theme.toast?.show({ type: 'success', message });
  }

  async function add(items, options = {}) {
    const payload = Array.isArray(items) ? { items } : { items: [items] };
    const sectionIds = buildSectionsParam(options.sections || getSectionsToRender());

    if (sectionIds?.length) {
      payload.sections = sectionIds.slice(0, MAX_SECTIONS_PER_REQUEST).join(',');
      payload.sections_url = getSectionsUrl(options.sectionsUrl);
    }

    let data;
    try {
      data = await request(theme.routes.cartAddUrl, {
        ...theme.utils.fetchConfig(),
        body: JSON.stringify(payload),
      });
    } catch (error) {
      const isStockError = error instanceof CartError && ['out_of_stock', 'stock_problems'].includes(error.type);
      if (isStockError && !options._retried) {
        const isSingleItem = !Array.isArray(items) || items.length === 1;
        const originalItem = Array.isArray(items) ? items[0] : items;
        const max = extractMaxFromDescription(error.originalError?.description || error.message || flattenErrors(error.originalError?.errors));
        if (isSingleItem && max > 0 && max < (originalItem.quantity ?? 1)) {
          try {
            const clampedItem = { ...originalItem, quantity: max };
            const retryResult = await add(Array.isArray(items) ? [clampedItem] : clampedItem, { ...options, _retried: true, _internal: true });

            const addedItem = retryResult.items?.find(i => i.variant_id === (originalItem.id || originalItem.variant_id));
            const lineKey = addedItem?.key;
            const clampMsg = (theme.locales?.cart?.lineError?.onlyNAvailable || '').replace('[count]', String(max));

            if (lineKey) errors.setLine(lineKey, clampMsg);
            const vid = originalItem.id || originalItem.variant_id;
            if (vid) errors.setProductForm(vid, clampMsg);

            theme.pubsub.publish(theme.pubsub.events.CART_ITEM_ERROR, {
              key: lineKey,
              variantId: vid,
              message: clampMsg,
              type: 'stock_clamped',
            });

            return retryResult;
          } catch (_) {}
        }
      }
      const originalItem = Array.isArray(items) ? items[0] : items;
      const variantId = originalItem?.id || originalItem?.variant_id || options._variantId;
      publishError(error, { lineKey: options._lineKey, variantId });
      await autoSyncCart();
      throw error;
    }

    if (data.sections) cacheSections(data.sections);

    const addedItems = data.items || [data];

    theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, { action: 'add', cart: data, sections: data.sections });
    theme.pubsub.publish(theme.pubsub.events.CART_ADD, { items: addedItems, sections: data.sections });

    if (!options._retried) {
      showSuccessToast(theme.locales?.cart?.success?.itemAdded || theme.locales?.cart?.toast?.itemAdded, options);
    }

    const drawerDefault = theme.settings?.cartDrawerEnable ?? false;
    const shouldOpenDrawer = options.openDrawer ?? drawerDefault;
    if (shouldOpenDrawer) {
      theme.pubsub.publish(theme.pubsub.events.CART_DRAWER_OPEN, {});
    }

    return data;
  }

  function change(key, quantity, options = {}) {
    if (pendingChanges.has(key)) {
      const pending = pendingChanges.get(key);
      pending.cancel();
    }

    return new Promise((resolve, reject) => {
      const controller = new AbortController();
      let timeoutId;

      const cancel = () => {
        clearTimeout(timeoutId);
        controller.abort();
        reject(new DOMException('Superseded', 'AbortError'));
      };

      pendingChanges.set(key, { cancel });

      timeoutId = setTimeout(async () => {
        pendingChanges.delete(key);

        const payload = { id: key, quantity };
        const sectionIds = buildSectionsParam(options.sections || getSectionsToRender());

        if (sectionIds?.length) {
          payload.sections = sectionIds.slice(0, MAX_SECTIONS_PER_REQUEST).join(',');
          payload.sections_url = getSectionsUrl(options.sectionsUrl);
        }

        let data;
        try {
          data = await request(theme.routes.cartChangeUrl, {
            ...theme.utils.fetchConfig(),
            body: JSON.stringify(payload),
            signal: controller.signal,
          });
        } catch (error) {
          if (error.name === 'AbortError') {
            reject(error);
            return;
          }
          const isStockError = error instanceof CartError && ['out_of_stock', 'stock_problems'].includes(error.type);
          if (isStockError && !options._retried) {
            const max = extractMaxFromDescription(error.originalError?.description || error.message || flattenErrors(error.originalError?.errors));
            if (max > 0 && max < quantity) {
              try {
                const retryResult = await change(key, max, { ...options, _retried: true, _internal: true });
                const clampMsg = (theme.locales?.cart?.lineError?.maxReached || '').replace('[count]', String(max));

                errors.setLine(key, clampMsg);

                theme.pubsub.publish(theme.pubsub.events.CART_ITEM_ERROR, {
                  key,
                  message: clampMsg,
                  type: 'stock_clamped',
                });

                resolve(retryResult);
                return;
              } catch (_) {}
            }
          }
          publishError(error, { lineKey: key });
          await autoSyncCart();
          reject(error);
          return;
        }

        if (data.sections) cacheSections(data.sections);

        const wasRemoved = quantity === 0 || !data.items?.find(item => item.key === key);

        if (!options._silent) {
          theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, { action: 'change', cart: data, sections: data.sections });

          if (wasRemoved) {
            theme.pubsub.publish(theme.pubsub.events.CART_REMOVE, { key, cart: data, sections: data.sections });
            showSuccessToast(theme.locales?.cart?.success?.itemRemoved, options);
          } else {
            showSuccessToast(theme.locales?.cart?.success?.quantityUpdated, options);
          }
        }

        resolve(data);
      }, 300);
    });
  }

  async function update(updates, options = {}) {
    const payload = { ...updates };
    const sectionIds = buildSectionsParam(options.sections || getSectionsToRender());

    if (sectionIds?.length) {
      payload.sections = sectionIds.slice(0, MAX_SECTIONS_PER_REQUEST).join(',');
      payload.sections_url = getSectionsUrl(options.sectionsUrl);
    }

    let data;
    try {
      data = await request(theme.routes.cartUpdateUrl, {
        ...theme.utils.fetchConfig(),
        body: JSON.stringify(payload),
      });
    } catch (error) {
      publishError(error, {});
      throw error;
    }

    if (data.sections) cacheSections(data.sections);

    theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, { action: 'update', cart: data, sections: data.sections });

    return data;
  }

  async function clear(options = {}) {
    let data;
    try {
      data = await request(theme.routes.cartClearUrl, {
        ...theme.utils.fetchConfig(),
      });
    } catch (error) {
      publishError(error, {});
      throw error;
    }

    theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, { action: 'clear', cart: data });
    theme.pubsub.publish(theme.pubsub.events.CART_CLEAR, { cart: data });

    showSuccessToast(theme.locales?.cart?.success?.cartCleared, options);

    return data;
  }

  async function fetchCart(options = {}) {
    let data;
    try {
      if (!cartInflight) {
        const pending = request(`${theme.routes.cartUrl}.js`, { method: 'GET' });
        cartInflight = pending;
        const release = () => {
          if (cartInflight === pending) cartInflight = null;
        };
        pending.then(release, release);
      }
      data = { ...(await cartInflight) };
    } catch (error) {
      publishError(error, { scope: 'silent' });
      throw error;
    }

    if (options.sections) {
      const sectionIds = buildSectionsParam(options.sections);
      if (sectionIds?.length) {
        const sections = await fetchSectionsChunked(sectionIds, options.sectionsUrl);
        cacheSections(sections);
        data.sections = sections;
      }
    }

    return data;
  }

  const _lineErrors = new Map();
  const _productFormErrors = new Map();
  let _footerError = null;

  const errors = {
    setLine(key, message) { _lineErrors.set(key, message); },
    clearLine(key) { _lineErrors.delete(key); },
    getLine(key) { return _lineErrors.get(key); },
    eachLine(cb) { _lineErrors.forEach((msg, key) => cb(key, msg)); },

    setFooter(message, type) { _footerError = { message, type }; },
    clearFooter() { _footerError = null; },
    getFooter() { return _footerError; },

    setProductForm(variantId, message) { _productFormErrors.set(String(variantId), message); },
    clearProductForm(variantId) { _productFormErrors.delete(String(variantId)); },
    getProductForm(variantId) { return _productFormErrors.get(String(variantId)); },
  };

  return {
    CartError,
    add,
    change,
    update,
    clear,
    fetch: fetchCart,
    getSectionsToRender,
    errorMessage,
    errors,
  };
})();
